import React from 'react';
import { Link } from 'react-router-dom';
import { PageHero } from '../../components/ui/PageHero';
import { QuoteBanner } from '../../components/ui/QuoteBanner';
import { ReviewNote } from '../../components/ui/ReviewNote';
import { SectionHeading } from '../../components/ui/SectionHeading';
import { ButtonLink } from '../../components/ui/ButtonLink';
import { useSeo } from '../../hooks/useSeo';
import { pageMeta } from '../../data/seo';
import { companyRegistration } from '../../data/registrations';
import { companyTimeline } from '../../data/companyTimeline';
import { services } from '../../data/services';
import { company } from '../../data/site';
import { heroImages } from '../../data/images';
const aboutCrumbs = (label: string) => [{
  label: 'Home',
  to: '/'
}, {
  label: 'About Us'
}, {
  label
}];
export function CompanyBackground() {
  useSeo(pageMeta.companyBackground);
  return <>
      <PageHero title="Company background" intro="A citizen-owned construction company based in Gaborone, incorporated in Botswana in May 2007." image={heroImages.roads} breadcrumbs={aboutCrumbs('Company Background')} data-id="element-501" data-mp="4vyck7hmqfze" />

      <section aria-labelledby="history-title" className="bg-charcoal py-20 md:py-24" data-id="element-502" data-mp="n36ppu0fjroh">
        <div className="mx-auto grid max-w-site gap-14 px-5 md:px-8 lg:grid-cols-12" data-id="element-503" data-mp="11w0ey75i7m6c">
          <div className="lg:col-span-7" data-id="element-504" data-mp="x1w7lmsbtc4q">
            <SectionHeading id="history-title" label="Background" title="History and company formation" data-id="element-505" data-mp="1auc5682gkhy9" />
            <div className="mt-8 space-y-5 text-[17px] leading-relaxed text-steel-200" data-id="element-506" data-mp="15w0slpks3tcc">
              <p data-id="element-507" data-mp="1ksi1ma5hwvvq">
                Liftoff Investments (Pty) Ltd is a 100% citizen-owned construction company based in Gaborone. It was
                incorporated in Botswana in May 2007 under the Companies Registration Act, Chapter 42:01. The director and
                shareholder is {company.director}, a Motswana civil engineer who also serves as Managing Director.
              </p>
              <p data-id="element-508" data-mp="b3t8b4c0age0">
                The company provides civil, structural and building engineering construction services in Botswana, and
                describes itself as a civil / structural and mechanical contractor. It is committed to the development and
                empowerment of its employees, in line with the ideals of the Government and people of Botswana.
              </p>
              <p data-id="element-509" data-mp="p182emd7fccb">
                Its work supports integrated infrastructure development in rural and urban areas, through projects for
                government ministries, parastatals, mining companies and private developers.
              </p>
            </div>
          </div>
          <aside className="lg:col-span-5" data-id="element-510" data-mp="12x2a5228rvnu">
            <div className="border-t-[3px] border-brand-red bg-charcoal-900 p-7" data-id="element-511" data-mp="17ulmfwfdpyd5">
              <h3 className="font-display text-[20px] font-bold uppercase tracking-[0.08em] text-white" data-id="element-512" data-mp="1ctf29vi521gh">Company details</h3>
              <dl className="mt-5 divide-y divide-white/10" data-id="element-513" data-mp="1hrgmijraw6tj">
                {companyRegistration.map(row => <div key={row.label} className="py-3" data-id="element-514" data-mp="32b2745irqsj6">
                    <dt className="text-[12px] font-semibold uppercase tracking-[0.14em] text-steel-400" data-id="element-515" data-mp="2xbh6lqnyac4c">{row.label}</dt>
                    <dd className="mt-1 text-[16px] text-white" data-id="element-516" data-mp="2sbbbu1mdw9i1">{row.value}</dd>
                  </div>)}
              </dl>
            </div>
            <ReviewNote className="mt-4" data-id="element-517" data-mp="2nfe6eqcnsnk1">
              Check registration number CO 2007/7072 against the certificate of incorporation, and confirm that Obokeng
              Rabai is still the director and Managing Director.
            </ReviewNote>
          </aside>
        </div>
      </section>

      <section aria-labelledby="offered-title" className="bg-charcoal-950 py-20 md:py-24" data-id="element-518" data-mp="3b8w3fggj9m6c">
        <div className="mx-auto max-w-site px-5 md:px-8" data-id="element-519" data-mp="369t7clij6qai">
          <SectionHeading id="offered-title" label="Institutional experience" title="Services offered" intro="The services the company profile lists:" className="max-w-2xl" data-id="element-520" data-mp="7mxc2kkkthvr" />
          <ul className="mt-10 grid border-t border-white/15 md:grid-cols-2" data-id="element-521" data-mp="3jkmjqb6f7g7q">
            {services.map(service => <li key={service.id} className="border-b border-white/15 py-5 md:odd:pr-10 md:even:pl-10" data-id="element-522" data-mp="39klz8lwteu1y">
                <Link to={`/services#${service.id}`} className="group block" data-id="element-523" data-mp="3ej744yn0i1wq">
                  <span className="font-display text-[22px] font-bold uppercase text-white group-hover:text-brand-light" data-id="element-524" data-mp="3el9y1tracf6q">
                    {service.title}
                  </span>
                  <span className="mt-1 block text-[15px] text-steel-300" data-id="element-525" data-mp="b4t561vngetb">{service.summary}</span>
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      <section aria-labelledby="timeline-title" className="bg-charcoal py-20 md:py-24" data-id="element-526" data-mp="1b3vcx43fea8">
        <div className="mx-auto max-w-site px-5 md:px-8" data-id="element-527" data-mp="p0y71bcoewsw">
          <SectionHeading id="timeline-title" label="Experience" title="Documented project milestones" intro="Completion years as recorded in the company's project references." className="max-w-2xl" data-id="element-528" data-mp="ttrbrz85a54z" />
          <ol className="mt-12 border-l-2 border-white/15" data-id="element-529" data-mp="2o9gdpy31cw25">
            {companyTimeline.map(entry => <li key={entry.year} className="relative grid gap-2 pb-8 pl-8 md:grid-cols-[140px_1fr] md:gap-8" data-id="element-530" data-mp="2jalks3bctr4b">
                <span className="absolute -left-[7px] top-2 h-3 w-3 bg-brand-red" aria-hidden="true" data-id="element-531" data-mp="1vk95g7ds4s3p" />
                <p className="font-display text-[28px] font-extrabold uppercase leading-none text-white" data-id="element-532" data-mp="1qm7opt3tt4ve">{entry.year}</p>
                <ul className="space-y-1.5" data-id="element-533" data-mp="24m8m2ykvtu64">
                  {entry.items.map(item => <li key={item} className="text-[16px] leading-relaxed text-steel-200" data-id="element-534" data-mp="20isp214whrsw">
                      {item}
                    </li>)}
                </ul>
              </li>)}
          </ol>
          <ButtonLink to="/projects" variant="outline" showArrow className="mt-4" data-id="element-535" data-mp="3byr36jexr40z">
            Explore the Projects
          </ButtonLink>
        </div>
      </section>

      <section aria-labelledby="workforce-title" className="bg-charcoal-950 py-20" data-id="element-536" data-mp="2dm55n62jfwsq">
        <div className="mx-auto grid max-w-site gap-10 px-5 md:px-8 lg:grid-cols-[1fr_auto] lg:items-end" data-id="element-537" data-mp="2im17aynrjc54">
          <div data-id="element-538" data-mp="2mmuvlt54asyj">
            <SectionHeading id="workforce-title" label="People" title="Workforce" data-id="element-539" data-mp="2mncp4i0eat2r" />
            <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-steel-200" data-id="element-540" data-mp="1pyojym9s30ar">
              The company profile records a workforce of {company.permanentEmployees} permanent employees and{' '}
              {company.casualEmployees} casual employees who work on a contract basis, supported by a management and head
              office team.
            </p>
          </div>
          <ButtonLink to="/about/management-team" showArrow className="self-start" data-id="element-541" data-mp="177npobjlrsxj">
            Management & Technical Team
          </ButtonLink>
        </div>
      </section>

      <QuoteBanner data-id="element-542" data-mp="13p8132t8ssx5" />
    </>;
}